<script lang="ts">
	//Services
	import { push } from 'svelte-spa-router';
	//Stores
	import { edit } from 'src/stores/ui/edit_lists';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	//Helpers
	import { trimLastChar } from 'src/helpers/utilities';
	import {
		isLegacyCameraButtonCardLayout,
		isSpecialDashCameraLayout,
		normalizeCameraConfig,
	} from 'src/components/Dashboards/dash-camera-helpers';
	import type { DashCameraWidgetConfig, DashDeviceCardConfig, DashWidgetTypes } from '../dash-types';
	import { normalizeDeviceCardConfig } from '../dash-device-card-config';
	//Components
	import DashWeatherWidgetCard from './Components/DashWeatherWidgetCard.svelte';
	import DashAccInfoWidgetCard from './Components/DashAccInfoWidgetCard.svelte';
	import DashRoomWidgetCard from './Components/DashRoomWidgetCard.svelte';
	import DashGroupWidgetCard from './Components/DashGroupWidgetCard.svelte';
	import DashDeviceControlCard from './Components/DashDeviceWidgetCard/DashDeviceControlCard.svelte';
	import DashMediaWidgetCard from './Components/DashMediaWidgetCard.svelte';
	import DashChartWidgetCard from './Components/DashChartWidgetCard.svelte';
	import DashSceneWidgetCard from './Components/DashSceneWidgetCard.svelte';
	import DashAlarmWidgetCard from './Components/DashAlarmWidgetCard.svelte';
	import DashThermostatWidgetCard from './Components/DashThermostatWidgetCard.svelte';
	import DashLightWidgetCard from './Components/DashLightWidgetCard.svelte';
	import DashDeviceQuickControl from './Components/DashDeviceWidgetCard/DashDeviceQuickControl.svelte';
	import DashDeviceCard from './Components/DashDeviceWidgetCard/DashDeviceCard.svelte';
	import DashCameraWidgetCard from './Components/DashDeviceWidgetCard/DashCameraWidgetCard.svelte';
	import WidgetTypeDebugOutline from 'src/components/Dev/WidgetTypeDebugOutline.svelte';
	import DashEnergyInfoWidgetCard from './Components/Energy/Info/DashEnergyInfoWidgetCard.svelte';
	import DashEnergyKpiLiveWidgetCard from './Components/Energy/KpiLive/DashEnergyKpiLiveWidgetCard.svelte';
	import DashEnergyKpiPeriodWidgetCard from './Components/Energy/KpiPeriod/DashEnergyKpiPeriodWidgetCard.svelte';
	import DashEnergyTopConsumersWidgetCard from './Components/Energy/TopConsumers/DashEnergyTopConsumersWidgetCard.svelte';
	import DashEnergyConsumerMixWidgetCard from './Components/Energy/ConsumerMix/DashEnergyConsumerMixWidgetCard.svelte';
	import DashEnergyRoomChartWidgetCard from './Components/Energy/RoomChart/DashEnergyRoomChartWidgetCard.svelte';
	import DashEnergyRoomDistributionWidgetCard from './Components/Energy/RoomDistribution/DashEnergyRoomDistributionWidgetCard.svelte';
	import DashEnergyExploreWidgetCard from './Components/Energy/Explore/DashEnergyExploreWidgetCard.svelte';

	import DashEnergyFlowWidgetCard from './Components/Energy/Flow/DashEnergyFlowWidgetCard.svelte';
	import DashEnergyHeatmapWidgetCard from './Components/Energy/Heatmap/DashEnergyHeatmapWidgetCard.svelte';
	import DashEnergyPeakWidgetCard from './Components/Energy/Peak/DashEnergyPeakWidgetCard.svelte';
	import DashEnergyInsightsWidgetCard from './Components/Energy/Insights/DashEnergyInsightsWidgetCard.svelte';
	import DashEnergyGenerationWidgetCard from './Components/Energy/Generation/DashEnergyGenerationWidgetCard.svelte';
	import DashEnergyPvDistributionWidgetCard from './Components/Energy/PvDistribution/DashEnergyPvDistributionWidgetCard.svelte';
	import DashEnergyTariffWidgetCard from './Components/Energy/Tariff/DashEnergyTariffWidgetCard.svelte';
	import DashEnergyPhaseWidgetCard from './Components/Energy/Phase/DashEnergyPhaseWidgetCard.svelte';
	import DashEnergyEnvironmentalWidgetCard from './Components/Energy/Environmental/DashEnergyEnvironmentalWidgetCard.svelte';
	import DashEnergyDeviceTrendWidgetCard from './Components/Energy/DeviceTrend/DashEnergyDeviceTrendWidgetCard.svelte';
	import DashEnergyRoomChartsWidgetCard from './Components/Energy/RoomCharts/DashEnergyRoomChartsWidgetCard.svelte';
	import EnergyWidgetVersionBadge from './Components/Energy/_shared/EnergyWidgetVersionBadge.svelte';
	import {
		getEnergyWidgetComponentName,
		getEnergyWidgetVersion,
	} from './Components/Energy/energy-widget-versions';
	import { energyWidgetVersionsEnabled } from 'src/stores/features/dev/energy-widget-versions';
	import DashEnvironmentWidgetCard from './Components/DashEnvironmentWidgetCard.svelte';
	import DashPromoWidgetCard from './Components/DashPromoWidgetCard.svelte';
	import type { DashEnergyWidgetConfig } from '../dash-energy-types';
	import { isDashEnergyWidgetType } from '../dash-energy-types';
	import { getEnergyConfigForType } from '../dash-energy-helpers';
	import EmWidgetState from 'src/components/EnergyMonitoring/EmWidgetState.svelte';
	import { Container } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import type { EnergyWidgetPreviewState } from 'src/components/EnergyMonitoring/v2/all-energy-widgets';
	import type { DashboardPromoBannerConfig } from 'src/configs/dashboards/energy-dashboard-promo-banners';

	export let cards: any;
	export let type: DashWidgetTypes;
	export let cameraConfig: DashCameraWidgetConfig = {};
	export let deviceCardConfig: DashDeviceCardConfig | undefined;
	export let energyConfig: DashEnergyWidgetConfig | undefined;
	export let staticPreview = false;
	export let widgetId = '';
	export let title = '';
	export let useEnergyPageContext = false;
	export let energyPreviewState: EnergyWidgetPreviewState = 'live';
	export let promoBanners: DashboardPromoBannerConfig[] = [];

	$: normalizedCameraConfig = normalizeCameraConfig(cameraConfig);
	$: controlPlacement = normalizeDeviceCardConfig(deviceCardConfig).controlPlacement;
	$: cameraVariant = normalizedCameraConfig.variant;
	$: useSpecialCameraLayout = type === 'dashCamera' && isSpecialDashCameraLayout(cameraVariant);
	$: useLegacyCameraButtonCards =
		type === 'dashCamera' && isLegacyCameraButtonCardLayout(cameraVariant);
	$: forcedEnergyState =
		isDashEnergyWidgetType(type) && !['live', 'sample'].includes(energyPreviewState);
	$: galleryStaticPreview = staticPreview || energyPreviewState === 'sample';
	$: previewStateKind = (
		energyPreviewState === 'error' ? 'error' : energyPreviewState === 'empty' ? 'empty' : 'setup'
	) as 'error' | 'empty' | 'setup';
	$: previewStateTitle =
		energyPreviewState === 'partial'
			? $format('energy_widget_gallery_partial_title', {
					default: 'Setup incomplete',
				})
			: energyPreviewState === 'empty'
				? $format('energy_widget_gallery_empty_title', {
						default: 'Nothing to display',
					})
				: '';
	$: previewStateMessage =
		energyPreviewState === 'partial'
			? $format('energy_widget_gallery_partial_message', {
					default:
						'Some data is available, but more settings are needed for the complete view.',
				})
			: energyPreviewState === 'setup'
				? $format('energy_widget_gallery_setup_message', {
						default: 'Required energy settings have not been configured yet.',
					})
				: energyPreviewState === 'empty'
					? $format('energy_widget_gallery_empty_message', {
							default: 'There is no live activity or history to show right now.',
						})
					: $format('energy_widget_gallery_error_message', {
							default: 'The widget could not load its data. Try again.',
						});

	const retryPreviewState = () => undefined;

	let componentMap: Record<
		Exclude<DashWidgetTypes, 'dashCamera'>,
		{
			component: any;
			getProps: (card: any) => Record<string, any>;
			noIteration?: boolean;
		}
	>;
	$: componentMap = {
		dashInfo: {
			component: DashAccInfoWidgetCard,
			getProps: () => ({}),
			noIteration: true,
		},
		dashGroup: {
			component: DashGroupWidgetCard,
			getProps: (card) => ({ groupWidget: card }),
		},
		dashScene: {
			component: DashSceneWidgetCard,
			getProps: (card) => ({ sceneWidget: card }),
		},
		dashAlarm: {
			component: DashAlarmWidgetCard,
			getProps: (card) => ({ alarmWidget: card }),
		},
		dashParameterDev: {
			// component: DashDeviceParamCard,
			component: DashDeviceQuickControl,
			getProps: (card) => ({ deviceWidget: card }),
		},
		dashControlDev: {
			component: DashDeviceControlCard,
			getProps: (card) => ({ deviceWidget: card }),
		},
		dashQuickDev: {
			component: DashDeviceQuickControl,
			getProps: (card) => ({ deviceWidget: card, cameraConfig, controlPlacement }),
		},
		// Legacy type: load as Control card (whole-card tap). Prefer remapping at load.
		dashCardDev: {
			component: DashDeviceQuickControl,
			getProps: (card) => ({
				deviceWidget: card,
				cameraConfig,
				controlPlacement: 'card' as const,
			}),
		},
		dashRoom: {
			component: DashRoomWidgetCard,
			getProps: (card) => ({ roomWidget: card }),
		},
		dashWeather: { component: DashWeatherWidgetCard, getProps: () => ({}) },
		dashChart: { component: DashChartWidgetCard, getProps: () => ({}) },
		dashMedia: {
			component: DashMediaWidgetCard,
			getProps: (card) => ({ mediaWidget: card }),
		},
		dashThermostat: {
			component: DashThermostatWidgetCard,
			getProps: (card) => ({ thermostatWidget: card }),
		},
		dashLight: {
			component: DashLightWidgetCard,
			getProps: (card) => ({ lightWidget: card }),
		},
		dashEnergyInfo: {
			component: DashEnergyInfoWidgetCard,
			getProps: () => ({
				energyConfig: getEnergyConfigForType('dashEnergyInfo', energyConfig),
				staticPreview,
			}),
			noIteration: true,
		},
		dashEnergyKpiLive: {
			component: DashEnergyKpiLiveWidgetCard,
			getProps: () => ({
				energyConfig: getEnergyConfigForType('dashEnergyKpiLive', energyConfig),
				staticPreview,
			}),
			noIteration: true,
		},
		dashEnergyKpiPeriod: {
			component: DashEnergyKpiPeriodWidgetCard,
			getProps: () => ({
				energyConfig: getEnergyConfigForType('dashEnergyKpiPeriod', energyConfig),
				staticPreview,
			}),
			noIteration: true,
		},
		dashEnergyTopConsumers: {
			component: DashEnergyTopConsumersWidgetCard,
			getProps: () => ({
				energyConfig: getEnergyConfigForType('dashEnergyTopConsumers', energyConfig),
				staticPreview,
			}),
			noIteration: true,
		},
		dashEnergyConsumerMix: {
			component: DashEnergyConsumerMixWidgetCard,
			getProps: () => ({
				energyConfig: getEnergyConfigForType('dashEnergyConsumerMix', energyConfig),
				staticPreview,
				showClassificationAction: useEnergyPageContext,
			}),
			noIteration: true,
		},
		dashEnergyRoomChart: {
			component: DashEnergyRoomChartWidgetCard,
			getProps: () => ({
				energyConfig: getEnergyConfigForType('dashEnergyRoomChart', energyConfig),
				staticPreview,
			}),
			noIteration: true,
		},
		dashEnergyFlow: {
			component: DashEnergyFlowWidgetCard,
			getProps: () => ({
				energyConfig: getEnergyConfigForType('dashEnergyFlow', energyConfig),
				staticPreview,
				useResponsiveDensity: useEnergyPageContext,
			}),
			noIteration: true,
		},
		dashEnergyHeatmap: {
			component: DashEnergyHeatmapWidgetCard,
			getProps: () => ({
				energyConfig: getEnergyConfigForType('dashEnergyHeatmap', energyConfig),
				staticPreview,
				widgetId,
				widgetTitle: title,
			}),
			noIteration: true,
		},
		dashEnergyPeak: {
			component: DashEnergyPeakWidgetCard,
			getProps: () => ({
				energyConfig: getEnergyConfigForType('dashEnergyPeak', energyConfig),
				staticPreview,
			}),
			noIteration: true,
		},
		dashEnergyInsights: {
			component: widgetId.startsWith('devices-explore')
				? DashEnergyExploreWidgetCard
				: DashEnergyInsightsWidgetCard,
			getProps: () => ({ staticPreview }),
			noIteration: true,
		},
		dashEnergyGeneration: {
			component: DashEnergyGenerationWidgetCard,
			getProps: () => ({
				energyConfig,
				useEnergyPageContext,
				staticPreview,
			}),
			noIteration: true,
		},
		dashEnergyPvDistribution: {
			component: DashEnergyPvDistributionWidgetCard,
			getProps: () => ({
				energyConfig,
				staticPreview,
			}),
			noIteration: true,
		},
		dashEnergyTariff: {
			component: DashEnergyTariffWidgetCard,
			getProps: () => ({
				energyConfig,
				staticPreview,
			}),
			noIteration: true,
		},
		dashEnergyPhase: {
			component: DashEnergyPhaseWidgetCard,
			getProps: () => ({
				energyConfig,
				staticPreview,
			}),
			noIteration: true,
		},
		dashEnergyEnvironmental: {
			component: DashEnergyEnvironmentalWidgetCard,
			getProps: () => ({
				energyConfig,
				staticPreview,
			}),
			noIteration: true,
		},
		dashEnergyDeviceTrend: {
			component: DashEnergyDeviceTrendWidgetCard,
			getProps: () => ({
				energyConfig,
				staticPreview,
			}),
			noIteration: true,
		},
		dashEnergyRoomCharts: {
			component: DashEnergyRoomChartsWidgetCard,
			getProps: () => ({
				energyConfig,
				staticPreview,
			}),
			noIteration: true,
		},
		dashEnergyRoomDistribution: {
			component: DashEnergyRoomDistributionWidgetCard,
			getProps: () => ({
				energyConfig: getEnergyConfigForType('dashEnergyRoomDistribution', energyConfig),
				staticPreview,
			}),
			noIteration: true,
		},
		dashEnvironment: {
			component: DashEnvironmentWidgetCard,
			getProps: () => ({ cards, staticPreview }),
			noIteration: true,
		},
		dashPromo: {
			component: DashPromoWidgetCard,
			getProps: () => ({ banners: promoBanners }),
			noIteration: true,
		},
	};

	$: energyWidgetVersion =
		isDashEnergyWidgetType(type) && $energyWidgetVersionsEnabled && !useEnergyPageContext
			? getEnergyWidgetVersion(type, widgetId)
			: undefined;
	$: energyWidgetComponentName =
		isDashEnergyWidgetType(type) && $energyWidgetVersionsEnabled
			? getEnergyWidgetComponentName(type, widgetId)
			: undefined;

	const onWidgetClick = (card, event: Event = null) => {
		if ($edit) return;
		if ((event?.target as Element)?.closest?.('[data-dashboard-widget-interactive]')) return;

		const id = card.id;
		let type = trimLastChar(card._type);
		type = type === 'temp_scheme' ? 'thermostat' : type;

		if (card._type === 'chart') {
			push(`/cons/0`);
			setSidePanelState({ open: false });
		} else if (type == 'room') {
			push(`/home/${type}/${id}/devices`);
			setSidePanelState({ open: false });
		} else {
			setSidePanelState({ type, id, open: true });
		}
	};
</script>

{#if forcedEnergyState}
	<WidgetTypeDebugOutline {type}>
		<div class="dash-energy-widget-frame">
			{#if energyWidgetVersion}
				<EnergyWidgetVersionBadge
					version={energyWidgetVersion}
					componentName={energyWidgetComponentName}
				/>
			{/if}
			<div class="dash-energy-widget-body">
				<Container type="glass" noScroll fullHeight>
					<EmWidgetState
						kind={previewStateKind}
						title={previewStateTitle}
						message={previewStateMessage}
						minHeight="180px"
						onRefresh={energyPreviewState === 'error' ? retryPreviewState : undefined}
					/>
				</Container>
			</div>
		</div>
	</WidgetTypeDebugOutline>
{:else if type === 'dashCamera'}
	{#if useSpecialCameraLayout}
		<WidgetTypeDebugOutline {type}>
			<DashCameraWidgetCard {cards} {cameraConfig} />
		</WidgetTypeDebugOutline>
	{:else if useLegacyCameraButtonCards}
		{#each cards as card}
			<WidgetTypeDebugOutline {type}>
				<DashDeviceCard
					deviceWidget={card}
					{cameraConfig}
					editMode={$edit}
					showFullscreen={!$edit}
					on:click={(event) => onWidgetClick(card, event)}
					on:arrow-click={() => onWidgetClick(card)}
					on:fullscreen={() => onWidgetClick(card)}
					on:handle-drag-start
					on:handle-drag-end
				/>
			</WidgetTypeDebugOutline>
		{/each}
	{/if}
{:else if componentMap[type]}
	{#if componentMap[type].noIteration}
		{#if isDashEnergyWidgetType(type)}
			<WidgetTypeDebugOutline {type}>
				<div class="dash-energy-widget-frame">
					{#if energyWidgetVersion}
						<EnergyWidgetVersionBadge
							version={energyWidgetVersion}
							componentName={energyWidgetComponentName}
						/>
					{/if}
					<div class="dash-energy-widget-body">
						<svelte:component
							this={componentMap[type].component}
							{...componentMap[type].getProps(cards?.[0])}
							staticPreview={galleryStaticPreview}
						/>
					</div>
				</div>
			</WidgetTypeDebugOutline>
		{:else}
			<WidgetTypeDebugOutline {type}>
				<svelte:component
					this={componentMap[type].component}
					{...componentMap[type].getProps(cards?.[0])}
				/>
			</WidgetTypeDebugOutline>
		{/if}
	{:else}
		{#each cards as card}
			<WidgetTypeDebugOutline {type}>
				<svelte:component
					this={componentMap[type].component}
					{...componentMap[type].getProps(card)}
					editMode={$edit}
					showFullscreen={!$edit}
					on:click={(event) => onWidgetClick(card, event)}
					on:arrow-click={() => onWidgetClick(card)}
					on:fullscreen={() => onWidgetClick(card)}
					on:handle-drag-start
					on:handle-drag-end
				/>
			</WidgetTypeDebugOutline>
		{/each}
	{/if}
{/if}

<style>
	.dash-energy-widget-frame {
		position: relative;
		display: flex;
		flex-direction: column;
		width: 100%;
		/* height: 100%; */
		min-width: 0;
		min-height: 0;
	}

	.dash-energy-widget-body {
		display: flex;
		flex-direction: column;
		flex: 1;
		min-width: 0;
		min-height: 0;
	}

	.dash-energy-widget-body :global(.container) {
		border-color: rgba(255, 255, 255, 0.12);
		border-style: solid;
		border-radius: 16px;
	}

	.dash-energy-widget-frame :global(.energy-widget-heading-text),
	.dash-energy-widget-frame :global(.em-heatmap-heading-text),
	.dash-energy-widget-frame :global(.em-minimal-heatmap-heading > div) {
		display: none;
	}

	.dash-energy-widget-frame :global(.em-info-tooltip-trigger) {
		display: none;
	}

	.dash-energy-widget-frame :global(.live-indicator-wrapper) {
		display: none;
	}

	.dash-energy-widget-frame :global(.em-minimal-heatmap-heading) {
		min-height: 0;
		justify-content: flex-end;
	}
</style>
